import { Button } from '@cloudflare/kumo';
import { ArrowClockwise, WarningCircle } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Component } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ApiError } from './api.ts';

export const previewDialogClassName =
  'm-0 p-0 fixed top-5 sm:top-5 left-5 w-[calc(100vw_-_40px)] sm:w-[calc(100vw_-_40px)] max-w-none h-[calc(100dvh_-_40px)] max-h-none overflow-hidden rounded-[12px] max-[768px]:top-0 sm:max-[768px]:top-0 max-[768px]:left-0 max-[768px]:w-screen sm:max-[768px]:w-screen max-[768px]:h-dvh max-[768px]:rounded-[0] translate-x-0';

export function ErrorNotice({ error, className = '' }: { error: unknown; className?: string }): ReactNode {
  const details =
    error instanceof ApiError &&
    error.detail &&
    typeof error.detail === 'object' &&
    'errors' in error.detail &&
    Array.isArray(error.detail.errors)
      ? error.detail.errors.flatMap((issue: unknown) =>
          issue && typeof issue === 'object' && 'message' in issue && typeof issue.message === 'string'
            ? [issue.message]
            : [],
        )
      : [];
  return error ? (
    <div
      className={`my-3 px-3.5 py-3 border border-kumo-danger/20 flex items-start gap-2 bg-kumo-danger-tint text-kumo-danger rounded-[8px] text-[13px] [&_svg]:shrink-0 ${className}`}
      role="alert"
    >
      <WarningCircle size={20} />
      <div>
        <span>{error instanceof Error ? error.message : 'Something went wrong. Please try again.'}</span>
        {details.length > 0 && (
          <ul>
            {[...new Set(details)].map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  ) : null;
}
export function Loading({ label = 'Loading…' }: { label?: string }): ReactNode {
  return (
    <div className="loading p-6 flex gap-2.5 items-center text-kumo-subtle text-[14px]" role="status">
      <span className="w-[7px] h-[7px] bg-kumo-fill rounded-full animate-breathe" />
      {label}
    </div>
  );
}
export const proseClasses =
  'prose leading-[1.75] text-[14px] [&_>_*_+_*]:mt-3 [&_ul]:list-disc [&_ul]:pl-5.5 [&_ol]:list-decimal [&_ol]:pl-5.5 [&_table]:border-collapse [&_table]:w-full [&_h1]:mt-4 [&_h1]:mb-2.5 [&_h1]:text-[24px] [&_h1]:leading-[1.2] [&_h1]:font-semibold [&_h1]:tracking-[-0.8px] [&_h2]:mt-4 [&_h2]:mb-2.5 [&_h2]:text-[19px] [&_h2]:font-semibold [&_h2]:tracking-[-0.35px] [&_h3]:mt-4 [&_h3]:mb-2.5 [&_h3]:text-[16px] [&_h3]:font-semibold [&_a]:underline [&_a]:underline-offset-[3px] [&_th]:px-2.5 [&_th]:py-[7px] [&_th]:border [&_th]:border-kumo-line [&_th]:text-left [&_td]:px-2.5 [&_td]:py-[7px] [&_td]:border [&_td]:border-kumo-line [&_td]:text-left [&_hr]:my-4.5 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-t-kumo-line';

const markdownPlugins = [remarkGfm];
const markdownComponents: Components = {
  a: ({ href, title, children }) => {
    const newTab =
      href && !href.startsWith('#') && (/^https?:/i.test(href) || !/^[a-z][a-z\d+.-]*:/i.test(href));
    return (
      <a
        href={href}
        title={title}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    );
  },
};

export function MarkdownView({ value }: { value: string }): ReactNode {
  return (
    <div className={`${proseClasses} overflow-x-auto`}>
      <ReactMarkdown remarkPlugins={markdownPlugins} components={markdownComponents}>
        {value}
      </ReactMarkdown>
    </div>
  );
}
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }
  override render(): ReactNode {
    if (this.state.error)
      return (
        <main className="px-6 py-17.5 grid justify-items-center gap-4 text-center text-kumo-subtle">
          <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">Something went wrong</h1>
          <p className="leading-[1.65]">Your saved work is safe. Reload to try again.</p>
          <Button onClick={() => location.reload()}>
            <ArrowClockwise />
            Reload
          </Button>
        </main>
      );
    return this.props.children;
  }
}
